/// <reference types="vite/types/importMeta" />
import { apiPathFn } from "@ntprsrf-frontend/common/src/utils/api";
import { Axios } from "axios-observable";
import { firstValueFrom, map, OperatorFunction, tap } from "rxjs";
import {
	GetFileListParams,
	GetFileListPathVariables,
	CreateFileShareRequestRequest,
	ShareFolderParams,
	BulkDownloadFileRequest,
	ShareFileWithCompany,
} from "../../requests/internet/eGuarantee.request";
import { ShareFileTypeMap } from "../../constants/share-files-type.constant";
import { CreateFileShareRequestResponse } from "../../responses/intranet/fileShare.response";
import { S3FileResponse } from "../../responses/internet/s3file.response";
import fileDownload from "js-file-download";
import { DOWNLOAD_CONFIG } from "@ntprsrf-frontend/common/src/utils/axios";
import { 
	GovernmentSetFavouriteRequest, 
	GovernmentServiceListParams 
} from "../../requests/internet/governmentService.request";
import { 
	GovernmentSetFavouriteResponse, 
	GovernmentServiceListResponse 
} from "../../responses/internet/governmentService.response";
import { GovernmentServiceDetailResponse } from "./../../responses/internet/governmentService.response";
import { AxiosError } from "axios";
import { corppassLogin } from "@ntprsrf-frontend/common/src/utils/corppass";

const apiPath = apiPathFn(import.meta.env.VITE_FILE_S3_SERVICE);
const apiPathFzFile = apiPathFn(import.meta.env.VITE_FZ_FILE_SERVICE);
const apiPathFileShareRequest = apiPathFn(
	import.meta.env.VITE_FILESHAREREQUEST_SERVICE,
);
const apiPathCompany = apiPathFn(import.meta.env.VITE_COMPANY_SERVICE);
const apiGovernmentPortal = apiPathFn(
	import.meta.env.VITE_GOVERNMENT_PORTAL_SERVICE,
);
const currentEnv = import.meta.env.VITE_ENVIRONMENT;

export function shareFile(params: ShareFileWithCompany, file: File) {
	let formData = new FormData();
	formData.append("file", file);

	if(currentEnv === "dev" || currentEnv === "staging"){
		// File Upload without scan
		return Axios.post(apiPath(["eGuarantee", "share-file"]), formData, {
			params: {
				...params,
			},
		});
	}else{
		// File Upload with scan
		return Axios.post(apiPathFzFile(["upload", "eGuarantee", "share-file"]), formData, {
			params: {
				...params,
			},
		});
	}
}
export function shareFileName(file: File) {
	let formData = new FormData();
	formData.append("file", file);

	if(currentEnv === "dev" || currentEnv === "staging"){
		// File Upload without scan
		return Axios.post(apiPath(["eGuarantee", "share-file-name"]), formData);
	}else{
		// File Upload with scan
		return Axios.post(apiPathFzFile(["upload", "eGuarantee", "share-file-name"]), formData);
	}
}

export function getFileList(
	params: GetFileListPathVariables,
	query: Partial<GetFileListParams>,
) {
	return Axios.get<S3FileResponse>(
		apiPath([
			"list-files",
			"eGuarantee",
			params.companyUEN,
			ShareFileTypeMap[params.sharePath],
		]),
		{
			params: query,
		},
	).pipe(map((res) => res.data));
}

export function createFileShareRequest(request: CreateFileShareRequestRequest) {
	return Axios.post<CreateFileShareRequestResponse>(
		apiPathFileShareRequest([]),
		request,
	).pipe(map((response) => response.data));
}

export function editEGuranteeAccess(companyId: string, params: Partial<any>) {
	return Axios.put<any>(apiPathCompany(["access", companyId]), params).pipe(
		map((response) => response.data),
	);
}

export function editEGuranteePrivilege(companyId: string, params: Partial<any>) {
	return Axios.put<any>(apiPathCompany(["privilege", companyId]), params).pipe(
		map((response) => response.data),
	);
}

export function bulkDownloadFiles(
	params: ShareFolderParams,
	requestBody: BulkDownloadFileRequest,
) {
	return Axios.post(
		apiPath([
			"bulk",
			"get-file",
			"eGuarantee",
			params.companyUEN,
			ShareFileTypeMap[params.sharePath],
		]),
		requestBody,
		DOWNLOAD_CONFIG,
	).pipe(
		tap((response) => {
			let filename = params.companyUEN + ".zip";
			fileDownload(response.data, filename);
		}),
	);
}

export function getGovtServiceList(
	query: Partial<GovernmentServiceListParams>,
) {
	return Axios.get<GovernmentServiceListResponse>(
		apiGovernmentPortal(["list"]),
		{
			params: query,
		},
	).pipe(map((res) => res.data));
}

export function setFavGovtService(request: GovernmentSetFavouriteRequest) {
	return Axios.post<GovernmentSetFavouriteResponse>(
		apiGovernmentPortal(["favourite"]),
		request,
	).pipe(map((response) => response.data));
}

export function getGovtServiceById(id: string) {
	return Axios.get<GovernmentServiceDetailResponse>(
		apiGovernmentPortal([id]),
	).pipe(map((res) => res.data));
}

export function getPopularGovtServiceList(
	query: Partial<GovernmentServiceListParams>,
) {
	return Axios.get<GovernmentServiceListResponse>(
		apiGovernmentPortal(["popular"]),
		{
			params: query,
		},
	).pipe(map((res) => res.data));
}

export function getRecentGovtServiceList(
	query: Partial<GovernmentServiceListParams>,
) {
	return Axios.get<GovernmentServiceListResponse>(
		apiGovernmentPortal(["recent"]),
		{
			params: query,
		},
	).pipe(map((res) => res.data));
}

export async function gotoRedirectUrl(
	id: string,
	loadingFn: (
		res: string,
	) => OperatorFunction<
		GovernmentServiceDetailResponse,
		GovernmentServiceDetailResponse
	>,
	loadingKeyPrefix: string = "",
	url: string = "",
	errorHandler?: (er: AxiosError) => void
) {
	try {
		const response = await firstValueFrom(
			getGovtServiceById(id).pipe(loadingFn(loadingKeyPrefix + id), map(res => res.data)),
		);
		return response;
	} catch (error) {
		if((error as AxiosError).response?.status === 401) {
			localStorage.setItem('serviceDetailId', id);
			await corppassLogin(url);
		}
		if (errorHandler) {
			errorHandler(error as AxiosError);
		}
		return null;
	}
}
